import * as React from 'react';

import {
  cleanDecimalInput,
  computeCursorAfterFormat,
  exceedsDecimalPrecision,
  formatCanonicalForDisplay,
  formatLiveDraft,
  isPartialDecimalInput,
  normalizeCanonicalNumeric,
  parseCanonicalNumeric,
} from '@packages/shared/forms';
import type { FieldDef } from '@packages/core';

export interface EditPrecisionConfig {
  storagePrecision: number;
  displayPrecision: number;
  displayFormat?: FieldDef['displayFormat'];
}

export interface CanonicalNumericFieldHandlers {
  displayValue: string;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (raw: string, cursorPos?: number) => void;
  inputRef: React.RefCallback<HTMLInputElement>;
}

export function useCanonicalNumericField(
  canonicalValue: unknown,
  onCanonicalChange: (value: number | string) => void,
  config: EditPrecisionConfig,
): CanonicalNumericFieldHandlers {
  const { storagePrecision, displayPrecision, displayFormat } = config;
  const [focused, setFocused] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const inputElRef = React.useRef<HTMLInputElement | null>(null);
  const pendingCursorRef = React.useRef<number | null>(null);

  const canonical = React.useMemo(
    () => normalizeCanonicalNumeric(canonicalValue, storagePrecision),
    [canonicalValue, storagePrecision],
  );

  const displayValue = React.useMemo(() => {
    if (focused) return draft;
    if (canonical === '') return '';
    return formatCanonicalForDisplay(canonical, displayPrecision, displayFormat);
  }, [focused, draft, canonical, displayPrecision, displayFormat]);

  React.useLayoutEffect(() => {
    if (pendingCursorRef.current != null && inputElRef.current) {
      const pos = pendingCursorRef.current;
      inputElRef.current.setSelectionRange(pos, pos);
      pendingCursorRef.current = null;
    }
  });

  const inputRef: React.RefCallback<HTMLInputElement> = React.useCallback((el) => {
    inputElRef.current = el;
  }, []);

  const onFocus = React.useCallback(() => {
    setFocused(true);
    const raw = canonical === '' ? '' : String(canonical);
    setDraft(formatLiveDraft(raw));
  }, [canonical]);

  const onBlur = React.useCallback(() => {
    setFocused(false);
    const cleaned = cleanDecimalInput(draft);
    const parsed = parseCanonicalNumeric(cleaned, storagePrecision);
    const committed = parsed === '' ? '' : parsed;
    if (!Object.is(committed, canonical)) {
      onCanonicalChange(committed);
    }
    setDraft('');
  }, [draft, onCanonicalChange, storagePrecision, canonical]);

  const onChange = React.useCallback(
    (raw: string, cursorPos?: number) => {
      const cleaned = cleanDecimalInput(raw);
      if (!isPartialDecimalInput(cleaned)) return;
      if (exceedsDecimalPrecision(cleaned, storagePrecision)) return;

      const formatted = formatLiveDraft(cleaned);
      setDraft(formatted);

      if (cursorPos != null) {
        pendingCursorRef.current = computeCursorAfterFormat(raw, formatted, cursorPos);
      }

      if (cleaned === '') {
        onCanonicalChange('');
        return;
      }

      if (cleaned === '.' || cleaned.endsWith('.')) {
        return;
      }

      const parsed = parseCanonicalNumeric(cleaned, storagePrecision);
      if (parsed !== '') {
        onCanonicalChange(parsed);
      }
    },
    [onCanonicalChange, storagePrecision],
  );

  return { displayValue, onFocus, onBlur, onChange, inputRef };
}
